import React, { useEffect, useState, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
    DollarSign, Lock, Unlock, Clock, User, ChevronDown,
    CreditCard, AlertCircle, TrendingUp, TrendingDown, Minus,
    ShoppingBag, XCircle, CheckCircle, RefreshCw
} from 'lucide-react';
import { useUser } from 'context/user/User';
import cashierSessionsApi from 'api/requests/cashierSessions';
import posEventsApi from 'api/requests/posEvents';
import BottomSheet from 'components/_UI/BottomSheet';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

/* ─── Helpers ─── */
function fmtCurrency(v) {
    return currency.format(Number(v) || 0);
}

function fmtDate(d) {
    return dayjs(d).format('DD/MM/YYYY');
}

function fmtTime(d) {
    return dayjs(d).format('HH:mm');
}

function fmtDateTime(d) {
    return dayjs(d).format('DD/MM/YYYY [às] HH:mm');
}

function elapsed(d) {
    return dayjs(d).fromNow(true);
}

function parseCurrencyInput(raw) {
    const cleaned = raw.replace(/[^\d]/g, '');
    return (parseInt(cleaned, 10) || 0) / 100;
}

function formatCurrencyInput(value) {
    if (!value && value !== 0) return '';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ─── Payment method icons/colors ─── */
const METHOD_STYLES = {
    'Dinheiro':           { color: '#2BC866', bg: '#2BC86612' },
    'Cartão de Crédito':  { color: '#3F6BC6', bg: '#3F6BC612' },
    'Cartão de Débito':   { color: '#5B8FE8', bg: '#5B8FE812' },
    'Pix':                { color: '#8025e8', bg: '#8025e812' },
};

function getMethodStyle(name) {
    return METHOD_STYLES[name] || { color: colors.night, bg: `${colors.night}12` };
}

/* ═══════════════════════════════════════════════════ */
/*                    MAIN COMPONENT                   */
/* ═══════════════════════════════════════════════════ */

export default function CashierPage() {
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;

    /* ── State ── */
    const [loading, setLoading] = useState(true);
    const [openSession, setOpenSession] = useState(null);
    const [summary, setSummary] = useState(null);
    const [history, setHistory] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [expandedSummary, setExpandedSummary] = useState({});

    /* Sheets */
    const [openSheet, setOpenSheet] = useState(false);
    const [closeSheet, setCloseSheet] = useState(false);

    /* Open form */
    const [openBalance, setOpenBalance] = useState('');
    const [openObs, setOpenObs] = useState('');
    const [opening, setOpening] = useState(false);
    const [openError, setOpenError] = useState('');

    /* Close form */
    const [closeBalance, setCloseBalance] = useState('');
    const [closeObs, setCloseObs] = useState('');
    const [closing, setClosing] = useState(false);
    const [closeError, setCloseError] = useState('');

    /* PosEvent toggle (open sheet) */
    const [openPosEvent, setOpenPosEvent] = useState(false);
    const [selectedPosEvent, setSelectedPosEvent] = useState(null);
    const [posEvents, setPosEvents] = useState([]);
    const [posEventsLoading, setPosEventsLoading] = useState(false);
    const [posEventsFetched, setPosEventsFetched] = useState(false);

    /* ── Data loading ── */
    const fetchData = useCallback(async () => {
        if (!slug) return;
        setLoading(true);
        try {
            const [openRes, historyRes] = await Promise.all([
                cashierSessionsApi.list(slug, { status: 'open' }),
                cashierSessionsApi.list(slug, { status: 'closed' }),
            ]);

            const openSessions = openRes.data.data || [];
            const current = openSessions[0] || null;
            setOpenSession(current);
            setHistory(historyRes.data.data || []);

            if (current) {
                try {
                    const sumRes = await cashierSessionsApi.summary(slug, current.id);
                    setSummary(sumRes.data.data || null);
                } catch { setSummary(null); }
            } else {
                setSummary(null);
            }
        } catch { }
        finally { setLoading(false); }
    }, [slug]);

    useEffect(() => { fetchData(); }, [fetchData]);

    /* ── Toggle PosEvent in open sheet ── */
    async function handleTogglePosEvent() {
        const next = !openPosEvent;
        setOpenPosEvent(next);
        if (next && !posEventsFetched) {
            setPosEventsLoading(true);
            try {
                const { data } = await posEventsApi.list(slug, { is_active: true });
                setPosEvents(data.data || data || []);
                setPosEventsFetched(true);
            } catch {}
            finally { setPosEventsLoading(false); }
        }
    }

    /* ── Open cashier ── */
    async function handleOpen() {
        const balanceValue = parseCurrencyInput(openBalance);
        setOpening(true);
        setOpenError('');
        try {
            await cashierSessionsApi.open(slug, {
                opening_balance: balanceValue,
                observation: openObs.trim() || null,
                ...(openPosEvent && selectedPosEvent && { pos_event_id: selectedPosEvent.id }),
            });
            setOpenSheet(false);
            setOpenBalance('');
            setOpenObs('');
            await fetchData();
        } catch (err) {
            setOpenError(err.response?.data?.message || 'Erro ao abrir o caixa.');
        } finally { setOpening(false); }
    }

    /* ── Close cashier ── */
    async function handleClose() {
        const balanceValue = parseCurrencyInput(closeBalance);
        if (!balanceValue && balanceValue !== 0) return;
        setClosing(true);
        setCloseError('');
        try {
            await cashierSessionsApi.close(slug, openSession.id, {
                closing_balance: balanceValue,
                observation: closeObs.trim() || null,
            });
            setCloseSheet(false);
            setCloseBalance('');
            setCloseObs('');
            await fetchData();
        } catch (err) {
            setCloseError(err.response?.data?.message || 'Erro ao fechar o caixa.');
        } finally { setClosing(false); }
    }

    /* ── Expand history row ── */
    async function toggleExpand(session) {
        if (expandedId === session.id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(session.id);
        if (!expandedSummary[session.id]) {
            try {
                const res = await cashierSessionsApi.summary(slug, session.id);
                setExpandedSummary(prev => ({ ...prev, [session.id]: res.data.data }));
            } catch { }
        }
    }

    /* ── Currency input handler ── */
    function handleCurrencyChange(setter) {
        return (e) => {
            const raw = e.target.value;
            const numeric = parseCurrencyInput(raw);
            setter(formatCurrencyInput(numeric));
        };
    }

    /* ── Balance difference ── */
    function getDifference(session) {
        const expected = Number(session.expected_cash_balance) || 0;
        const closing = Number(session.closing_balance) || 0;
        return closing - expected;
    }

    if (loading) {
        return (
            <Page>
                <LoadingWrap>
                    <Spinner />
                    <LoadingLabel>Carregando caixa...</LoadingLabel>
                </LoadingWrap>
            </Page>
        );
    }

    return (
        <Page>
            {/* ═══ STATUS BANNER ═══ */}
            {openSession ? (
                <StatusCard $open>
                    <StatusGlow />
                    <StatusTop>
                        <StatusIndicator $open>
                            <Unlock size={18} strokeWidth={2.5} />
                        </StatusIndicator>
                        <StatusInfo>
                            <StatusLabelRow>
                                <StatusLabel $open>Caixa Aberto</StatusLabel>
                                <PulseRing />
                            </StatusLabelRow>
                            <StatusMeta>
                                <MetaItem>
                                    <User size={13} />
                                    {openSession.opened_by?.name}
                                </MetaItem>
                                <MetaItem>
                                    <Clock size={13} />
                                    {fmtTime(openSession.opened_at)} &middot; {elapsed(openSession.opened_at)}
                                </MetaItem>
                            </StatusMeta>
                        </StatusInfo>
                    </StatusTop>

                    <StatusBalanceRow>
                        <BalanceBlock>
                            <BalanceLabel>Saldo inicial</BalanceLabel>
                            <BalanceValue>{fmtCurrency(openSession.opening_balance)}</BalanceValue>
                        </BalanceBlock>
                        {summary && (
                            <BalanceBlock $right>
                                <BalanceLabel>Saldo esperado</BalanceLabel>
                                <BalanceValue $accent>{fmtCurrency(summary.expected_balance)}</BalanceValue>
                            </BalanceBlock>
                        )}
                    </StatusBalanceRow>

                    {openSession.observation && (
                        <ObsTag>{openSession.observation}</ObsTag>
                    )}

                    <CloseBtn onClick={() => setCloseSheet(true)}>
                        <Lock size={16} strokeWidth={2.5} />
                        Fechar Caixa
                    </CloseBtn>
                </StatusCard>
            ) : (
                <StatusCard>
                    <StatusTop>
                        <StatusIndicator>
                            <Lock size={18} strokeWidth={2.5} />
                        </StatusIndicator>
                        <StatusInfo>
                            <StatusLabel>Nenhum caixa aberto</StatusLabel>
                            <StatusHint>Abra um caixa para registrar movimentações.</StatusHint>
                        </StatusInfo>
                    </StatusTop>
                    <OpenBtn onClick={() => { setOpenSheet(true); setOpenError(''); }}>
                        <Unlock size={16} strokeWidth={2.5} />
                        Abrir Caixa
                    </OpenBtn>
                </StatusCard>
            )}

            {/* ═══ SESSION SUMMARY ═══ */}
            {openSession && summary && (
                <SummarySection>
                    <SummaryGrid>
                        <SummaryCard>
                            <SummaryIcon $color={colors.success}>
                                <DollarSign size={18} />
                            </SummaryIcon>
                            <SummaryData>
                                <SummaryCardLabel>Total de vendas</SummaryCardLabel>
                                <SummaryCardValue>{fmtCurrency(summary.total_sales)}</SummaryCardValue>
                            </SummaryData>
                        </SummaryCard>

                        <SummaryCard>
                            <SummaryIcon $color={colors.primary}>
                                <ShoppingBag size={18} />
                            </SummaryIcon>
                            <SummaryData>
                                <SummaryCardLabel>Comandas</SummaryCardLabel>
                                <SummaryCardValue>{summary.total_commands}</SummaryCardValue>
                            </SummaryData>
                        </SummaryCard>

                        <SummaryCard>
                            <SummaryIcon $color={colors.occasionalOrange}>
                                <TrendingUp size={18} />
                            </SummaryIcon>
                            <SummaryData>
                                <SummaryCardLabel>Vendas em aberto</SummaryCardLabel>
                                <SummaryCardValue>{fmtCurrency(summary.total_open_sales)}</SummaryCardValue>
                            </SummaryData>
                        </SummaryCard>

                        <SummaryCard>
                            <SummaryIcon $color={colors.occasionalGreen}>
                                <CheckCircle size={18} />
                            </SummaryIcon>
                            <SummaryData>
                                <SummaryCardLabel>Comandas fechadas</SummaryCardLabel>
                                <SummaryCardValue>{summary.closed_commands}</SummaryCardValue>
                            </SummaryData>
                        </SummaryCard>

                        <SummaryCard>
                            <SummaryIcon $color={colors.occasionalGreen}>
                                <TrendingDown size={18} />
                            </SummaryIcon>
                            <SummaryData>
                                <SummaryCardLabel>Vendas finalizadas</SummaryCardLabel>
                                <SummaryCardValue>{fmtCurrency(summary.total_closed_sales)}</SummaryCardValue>
                            </SummaryData>
                        </SummaryCard>

                        <SummaryCard>
                            <SummaryIcon $color={colors.danger}>
                                <XCircle size={18} />
                            </SummaryIcon>
                            <SummaryData>
                                <SummaryCardLabel>Comandas canceladas</SummaryCardLabel>
                                <SummaryCardValue>{summary.cancelled_commands}</SummaryCardValue>
                            </SummaryData>
                        </SummaryCard>
                    </SummaryGrid>

                    {summary.payments_by_method?.length > 0 && (
                        <PaymentsCard>
                            <PaymentsTitle>
                                <CreditCard size={15} />
                                Pagamentos por método
                            </PaymentsTitle>
                            <PaymentsList>
                                {summary.payments_by_method.map((pm, i) => {
                                    const style = getMethodStyle(pm.payment_method);
                                    const total = Number(pm.total) || 0;
                                    const salesTotal = Number(summary.total_sales) || 1;
                                    const pct = Math.round((total / salesTotal) * 100);
                                    return (
                                        <PaymentRow key={i}>
                                            <PaymentInfo>
                                                <PaymentDot $color={style.color} />
                                                <PaymentName>{pm.payment_method}</PaymentName>
                                            </PaymentInfo>
                                            <PaymentRight>
                                                <PaymentBar>
                                                    <PaymentBarFill $color={style.color} $width={pct} />
                                                </PaymentBar>
                                                <PaymentAmount>{fmtCurrency(pm.total)}</PaymentAmount>
                                            </PaymentRight>
                                        </PaymentRow>
                                    );
                                })}
                            </PaymentsList>
                            {summary.total_tips > 0 && (
                                <TipsRow>
                                    Gorjetas: <strong>{fmtCurrency(summary.total_tips)}</strong>
                                </TipsRow>
                            )}
                        </PaymentsCard>
                    )}
                </SummarySection>
            )}

            {/* ═══ HISTORY ═══ */}
            <HistorySection>
                <HistoryHeader>
                    <HistoryTitle>Histórico de Caixas</HistoryTitle>
                    <RefreshBtn onClick={fetchData} title="Atualizar">
                        <RefreshCw size={16} />
                    </RefreshBtn>
                </HistoryHeader>

                {history.length === 0 ? (
                    <EmptyHistory>
                        <EmptyTitle>Nenhum caixa encerrado</EmptyTitle>
                        <EmptyHint>O histórico aparecerá aqui quando sessões forem fechadas.</EmptyHint>
                    </EmptyHistory>
                ) : (
                    <HistoryList>
                        {history.map(session => {
                            const diff = getDifference(session);
                            const isExpanded = expandedId === session.id;
                            const sessSummary = expandedSummary[session.id];

                            return (
                                <HistoryItem key={session.id} onClick={() => toggleExpand(session)}>
                                    <HistoryRow>
                                        <HistoryLeft>
                                            <HistoryDate>{fmtDate(session.opened_at)}</HistoryDate>
                                            <HistoryTime>
                                                {fmtTime(session.opened_at)} — {fmtTime(session.closed_at)}
                                            </HistoryTime>
                                            <HistoryUser>
                                                <User size={12} />
                                                {session.opened_by?.name}
                                            </HistoryUser>
                                            {session.pos_event?.name && (
                                                <EventBadge>{session.pos_event.name}</EventBadge>
                                            )}
                                        </HistoryLeft>
                                        <HistoryRight>
                                            <HistoryBalances>
                                                <HistoryBalanceSmall>
                                                    Abertura: {fmtCurrency(session.opening_balance)}
                                                </HistoryBalanceSmall>
                                                <HistoryClosingBalance>
                                                    {fmtCurrency(session.closing_balance)}
                                                </HistoryClosingBalance>
                                            </HistoryBalances>
                                            <DiffBadge $positive={diff >= 0}>
                                                {diff > 0 ? <TrendingUp size={12} /> : diff < 0 ? <TrendingDown size={12} /> : <Minus size={12} />}
                                                {diff >= 0 ? '+' : ''}{fmtCurrency(diff)}
                                            </DiffBadge>
                                            <ExpandIcon $expanded={isExpanded}>
                                                <ChevronDown size={18} />
                                            </ExpandIcon>
                                        </HistoryRight>
                                    </HistoryRow>

                                    {isExpanded && (
                                        <ExpandedContent onClick={e => e.stopPropagation()}>
                                            {session.observation && (
                                                <ExpandedObs>"{session.observation}"</ExpandedObs>
                                            )}
                                            <ExpandedGrid>
                                                <ExpandedStat>
                                                    <ExpandedStatLabel>Abertura</ExpandedStatLabel>
                                                    <ExpandedStatValue>{fmtCurrency(session.opening_balance)}</ExpandedStatValue>
                                                </ExpandedStat>
                                                <ExpandedStat>
                                                    <ExpandedStatLabel>Esperado</ExpandedStatLabel>
                                                    <ExpandedStatValue>{fmtCurrency(session.expected_balance)}</ExpandedStatValue>
                                                </ExpandedStat>
                                                <ExpandedStat>
                                                    <ExpandedStatLabel>Fechamento</ExpandedStatLabel>
                                                    <ExpandedStatValue $bold>{fmtCurrency(session.closing_balance)}</ExpandedStatValue>
                                                </ExpandedStat>
                                                <ExpandedStat>
                                                    <ExpandedStatLabel>Diferença</ExpandedStatLabel>
                                                    <ExpandedStatValue $diff={diff}>
                                                        {diff >= 0 ? '+' : ''}{fmtCurrency(diff)}
                                                    </ExpandedStatValue>
                                                </ExpandedStat>
                                            </ExpandedGrid>

                                            {session.closed_by && (
                                                <ExpandedMeta>
                                                    Fechado por <strong>{session.closed_by.name}</strong> em {fmtDateTime(session.closed_at)}
                                                </ExpandedMeta>
                                            )}

                                            {sessSummary && sessSummary.payments_by_method?.length > 0 && (
                                                <ExpandedPayments>
                                                    {sessSummary.payments_by_method.map((pm, i) => {
                                                        const style = getMethodStyle(pm.payment_method);
                                                        return (
                                                            <ExpandedPaymentRow key={i}>
                                                                <PaymentDot $color={style.color} />
                                                                <span>{pm.payment_method}</span>
                                                                <ExpandedPaymentValue>{fmtCurrency(pm.total)}</ExpandedPaymentValue>
                                                            </ExpandedPaymentRow>
                                                        );
                                                    })}
                                                    <ExpandedPaymentDivider />
                                                    <ExpandedPaymentRow>
                                                        <span>Total vendido</span>
                                                        <ExpandedPaymentValue>{fmtCurrency(sessSummary.total_sales)}</ExpandedPaymentValue>
                                                    </ExpandedPaymentRow>
                                                </ExpandedPayments>
                                            )}
                                        </ExpandedContent>
                                    )}
                                </HistoryItem>
                            );
                        })}
                    </HistoryList>
                )}
            </HistorySection>

            {/* ═══ OPEN CASHIER SHEET ═══ */}
            <BottomSheet
                isOpen={openSheet}
                onClose={() => { setOpenSheet(false); setOpenBalance(''); setOpenObs(''); setOpenError(''); setOpenPosEvent(false); setSelectedPosEvent(null); }}
                title="Abrir Caixa"
                size="large"
            >
                <SheetForm>
                    <FormField>
                        <FormLabel>Saldo inicial (R$)</FormLabel>
                        <CurrencyWrap>
                            <CurrencyPrefix>R$</CurrencyPrefix>
                            <CurrencyInput
                                placeholder="0,00"
                                value={openBalance}
                                autoFocus
                                inputMode="numeric"
                                onChange={handleCurrencyChange(setOpenBalance)}
                                onKeyDown={e => e.key === 'Enter' && !opening && handleOpen()}
                            />
                        </CurrencyWrap>
                        <FormHint>Valor em dinheiro no caixa ao iniciar.</FormHint>
                    </FormField>

                    <FormField>
                        <FormLabel>Observação <FormOptional>(opcional)</FormOptional></FormLabel>
                        <FormTextarea
                            placeholder="Ex: Início do turno da noite"
                            value={openObs}
                            onChange={e => setOpenObs(e.target.value)}
                            rows={2}
                        />
                    </FormField>

                    <ToggleRow onClick={handleTogglePosEvent}>
                        <ToggleLabel>
                            <ToggleTitle>Vincular a um evento?</ToggleTitle>
                            <ToggleHint>Registra vendas de ingressos desta sessão</ToggleHint>
                        </ToggleLabel>
                        <TipSwitch $active={openPosEvent}>
                            <TipSwitchKnob $active={openPosEvent} />
                        </TipSwitch>
                    </ToggleRow>

                    {openPosEvent && (
                        <PosEventPicker>
                            {posEventsLoading
                                ? <PickerHint>Carregando eventos...</PickerHint>
                                : posEvents.length === 0
                                    ? <PickerHint>Nenhum evento ativo. Crie um em Estabelecimento → Eventos.</PickerHint>
                                    : posEvents.map(ev => (
                                        <PosEventOption
                                            key={ev.id}
                                            type="button"
                                            $selected={selectedPosEvent?.id === ev.id}
                                            onClick={() => setSelectedPosEvent(ev)}
                                        >
                                            {ev.name}
                                        </PosEventOption>
                                    ))
                            }
                        </PosEventPicker>
                    )}

                    {openError && (
                        <ErrorMsg>
                            <AlertCircle size={14} />
                            {openError}
                        </ErrorMsg>
                    )}

                    <SubmitBtn onClick={handleOpen} disabled={opening}>
                        {opening ? 'Abrindo...' : 'Confirmar Abertura'}
                    </SubmitBtn>
                </SheetForm>
            </BottomSheet>

            {/* ═══ CLOSE CASHIER SHEET ═══ */}
            <BottomSheet
                isOpen={closeSheet}
                onClose={() => { setCloseSheet(false); setCloseBalance(''); setCloseObs(''); setCloseError(''); }}
                title="Fechar Caixa"
                size="large"
            >
                <SheetForm>
                    {summary && (
                        <CloseExpectedRow>
                            <CloseExpectedLabel>Saldo esperado no caixa</CloseExpectedLabel>
                            <CloseExpectedValue>{fmtCurrency(summary.expected_cash_balance)}</CloseExpectedValue>
                            <CloseExpectedHint>
                                {fmtCurrency(openSession?.opening_balance)} (abertura) + {fmtCurrency(Number(summary.expected_cash_balance) - Number(openSession?.opening_balance))} (vendas em dinheiro)
                            </CloseExpectedHint>
                        </CloseExpectedRow>
                    )}

                    <FormField>
                        <FormLabel>Valor contado no caixa (R$)</FormLabel>
                        <CurrencyWrap>
                            <CurrencyPrefix>R$</CurrencyPrefix>
                            <CurrencyInput
                                placeholder="0,00"
                                value={closeBalance}
                                autoFocus
                                inputMode="numeric"
                                onChange={handleCurrencyChange(setCloseBalance)}
                                onKeyDown={e => e.key === 'Enter' && !closing && handleClose()}
                            />
                        </CurrencyWrap>
                        <FormHint>Conte o dinheiro físico e informe o total.</FormHint>
                    </FormField>

                    {closeBalance && summary && (() => {
                        const counted = parseCurrencyInput(closeBalance);
                        const expected = Number(summary.expected_cash_balance) || 0;
                        const diff = counted - expected;
                        if (Math.abs(diff) < 0.01) return null;
                        return (
                            <DiffPreview $positive={diff > 0}>
                                {diff > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                Diferença de <strong>{diff > 0 ? '+' : ''}{fmtCurrency(diff)}</strong> em relação ao esperado
                            </DiffPreview>
                        );
                    })()}

                    <FormField>
                        <FormLabel>Observação <FormOptional>(opcional)</FormOptional></FormLabel>
                        <FormTextarea
                            placeholder="Ex: Conferido, R$20 de gorjeta separada"
                            value={closeObs}
                            onChange={e => setCloseObs(e.target.value)}
                            rows={2}
                        />
                    </FormField>

                    {closeError && (
                        <ErrorMsg>
                            <AlertCircle size={14} />
                            {closeError}
                        </ErrorMsg>
                    )}

                    <SubmitBtn $danger onClick={handleClose} disabled={closing || !closeBalance}>
                        {closing ? 'Fechando...' : 'Confirmar Fechamento'}
                    </SubmitBtn>
                </SheetForm>
            </BottomSheet>
        </Page>
    );
}

/* ═══════════════════════════════════════════════════ */
/*                    STYLED COMPONENTS                */
/* ═══════════════════════════════════════════════════ */

const pulse = keyframes`
    0%   { transform: scale(1);   opacity: 1; }
    100% { transform: scale(2.2); opacity: 0; }
`;

const rotate = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const fadeIn = keyframes`from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); }`;

/* ─── Page ─── */
const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
`;

const LoadingWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 80px 0;
`;

const Spinner = styled.div`
    width: 32px; height: 32px;
    border: 3px solid ${colors.neutralDark};
    border-top-color: ${colors.occasionalPurple};
    border-radius: 50%;
    animation: ${rotate} 0.7s linear infinite;
`;

const LoadingLabel = styled.span`
    font-size: 14px; color: ${colors.nightLight};
`;

/* ─── Status Card ─── */
const StatusCard = styled.div`
    position: relative;
    background: #fff;
    border-radius: 16px;
    border: 1.5px solid ${({ $open }) => $open ? `${colors.success}40` : colors.neutralDark};
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow: hidden;
`;

const StatusGlow = styled.div`
    position: absolute;
    top: -40px; right: -40px;
    width: 120px; height: 120px;
    background: radial-gradient(circle, ${colors.success}15 0%, transparent 70%);
    pointer-events: none;
`;

const StatusTop = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const StatusIndicator = styled.div`
    position: relative;
    width: 42px; height: 42px;
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    background: ${({ $open }) => $open ? `${colors.success}14` : colors.neutralDark};
    color: ${({ $open }) => $open ? colors.success : colors.nightLight};
`;

const StatusLabelRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const PulseRing = styled.div`
    position: relative;
    width: 10px; height: 10px;
    flex-shrink: 0;
    border-radius: 50%;
    background: ${colors.success};
    &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: ${colors.success};
        animation: ${pulse} 2s ease-out infinite;
    }
`;

const StatusInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const StatusLabel = styled.span`
    font-size: 17px;
    font-weight: 800;
    color: ${({ $open }) => $open ? colors.success : colors.nightDark};
    letter-spacing: -0.2px;
`;

const StatusMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
`;

const MetaItem = styled.span`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    color: ${colors.night};
`;

const StatusHint = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
`;

const StatusBalanceRow = styled.div`
    display: flex;
    gap: 16px;
`;

const BalanceBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    ${({ $right }) => $right && css`text-align: right; margin-left: auto;`}
`;

const BalanceLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const BalanceValue = styled.span`
    font-size: 20px;
    font-weight: 800;
    color: ${({ $accent }) => $accent ? colors.success : colors.nightDark};
    letter-spacing: -0.5px;
    font-variant-numeric: tabular-nums;
`;

const ObsTag = styled.span`
    font-size: 12px;
    color: ${colors.night};
    font-style: italic;
    padding: 6px 10px;
    background: ${colors.neutral};
    border-radius: 8px;
    align-self: flex-start;
`;

const OpenBtn = styled.button`
    display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 14px;
    border-radius: 12px;
    background: ${colors.success};
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
`;

const CloseBtn = styled.button`
    display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 14px;
    border-radius: 12px;
    background: transparent;
    color: ${colors.danger};
    font-size: 15px;
    font-weight: 700;
    border: 1.5px solid ${colors.danger}40;
    cursor: pointer;
    transition: all 0.15s;
    &:hover { background: ${colors.danger}08; }
`;

/* ─── Summary Section ─── */
const SummarySection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    animation: ${fadeIn} 0.3s ease;
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
`;

const SummaryCard = styled.div`
    background: #fff;
    border: 1px solid ${colors.neutralDark};
    border-radius: 14px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
`;

const SummaryIcon = styled.div`
    width: 36px; height: 36px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    background: ${({ $color }) => `${$color}14`};
    color: ${({ $color }) => $color};
`;

const SummaryData = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

const SummaryCardLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const SummaryCardValue = styled.span`
    font-size: 17px;
    font-weight: 800;
    color: ${colors.nightDark};
    letter-spacing: -0.3px;
    font-variant-numeric: tabular-nums;
`;

/* ─── Payments Card ─── */
const PaymentsCard = styled.div`
    background: #fff;
    border: 1px solid ${colors.neutralDark};
    border-radius: 14px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const PaymentsTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const PaymentsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const PaymentRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const PaymentInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const PaymentDot = styled.div`
    width: 8px; height: 8px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
`;

const PaymentName = styled.span`
    font-size: 13px;
    color: ${colors.night};
    white-space: nowrap;
`;

const PaymentRight = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    justify-content: flex-end;
`;

const PaymentBar = styled.div`
    flex: 1;
    max-width: 100px;
    height: 4px;
    border-radius: 2px;
    background: ${colors.neutralDark};
    overflow: hidden;
`;

const PaymentBarFill = styled.div`
    height: 100%;
    border-radius: 2px;
    background: ${({ $color }) => $color};
    width: ${({ $width }) => Math.max($width, 4)}%;
    transition: width 0.5s ease;
`;

const PaymentAmount = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.nightDark};
    font-variant-numeric: tabular-nums;
    min-width: 80px;
    text-align: right;
`;

const TipsRow = styled.div`
    font-size: 12px;
    color: ${colors.night};
    padding-top: 10px;
    border-top: 1px solid ${colors.neutralDark};
`;

/* ─── History ─── */
const HistorySection = styled.div`
    background: #fff;
    border-radius: 14px;
    border: 1px solid ${colors.neutralDark};
    overflow: hidden;
`;

const HistoryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const HistoryTitle = styled.h3`
    font-size: 16px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0;
`;

const RefreshBtn = styled.button`
    width: 34px; height: 34px;
    border-radius: 10px;
    background: ${colors.neutral};
    border: none;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: ${colors.night};
    transition: opacity 0.15s;
    &:hover { opacity: 0.7; }
`;

const EmptyHistory = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 48px 24px;
    text-align: center;
    gap: 6px;
`;

const EmptyTitle = styled.p`font-weight: 700; font-size: 15px; color: ${colors.nightDark}; margin: 0;`;
const EmptyHint = styled.p`font-size: 13px; color: ${colors.nightLight}; margin: 0;`;

const HistoryList = styled.div`display: flex; flex-direction: column;`;

const HistoryItem = styled.div`
    border-bottom: 1px solid ${colors.neutralDark};
    cursor: pointer;
    transition: background 0.1s;
    &:last-child { border-bottom: none; }
    &:hover { background: ${colors.neutral}80; }
`;

const HistoryRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    gap: 12px;
`;

const HistoryLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
`;

const HistoryDate = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const HistoryTime = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const HistoryUser = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: ${colors.night};
`;

const HistoryRight = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
`;

const HistoryBalances = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
`;

const HistoryBalanceSmall = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
`;

const HistoryClosingBalance = styled.span`
    font-size: 15px;
    font-weight: 800;
    color: ${colors.nightDark};
    font-variant-numeric: tabular-nums;
`;

const DiffBadge = styled.span`
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 6px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    background: ${({ $positive }) => $positive ? `${colors.success}12` : `${colors.danger}12`};
    color: ${({ $positive }) => $positive ? colors.success : colors.danger};
`;

const ExpandIcon = styled.div`
    display: flex;
    color: ${colors.nightLight};
    transition: transform 0.2s;
    ${({ $expanded }) => $expanded && css`transform: rotate(180deg);`}
`;

/* ─── Expanded content ─── */
const ExpandedContent = styled.div`
    padding: 0 20px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    animation: ${fadeIn} 0.2s ease;
`;

const ExpandedObs = styled.p`
    font-size: 13px;
    color: ${colors.night};
    font-style: italic;
    margin: 0;
    padding: 8px 12px;
    background: ${colors.neutral};
    border-radius: 8px;
`;

const ExpandedGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
`;

const ExpandedStat = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 12px;
    background: ${colors.neutral};
    border-radius: 10px;
`;

const ExpandedStatLabel = styled.span`
    font-size: 10px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const ExpandedStatValue = styled.span`
    font-size: 15px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${({ $diff, $bold }) =>
        $diff !== undefined
            ? ($diff >= 0 ? colors.success : colors.danger)
            : ($bold ? colors.nightDark : colors.night)
    };
`;

const ExpandedMeta = styled.p`
    font-size: 12px;
    color: ${colors.nightLight};
    margin: 0;
`;

const ExpandedPayments = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 12px;
    background: ${colors.neutral};
    border-radius: 10px;
`;

const ExpandedPaymentRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${colors.night};
`;

const ExpandedPaymentValue = styled.span`
    margin-left: auto;
    font-weight: 700;
    color: ${colors.nightDark};
    font-variant-numeric: tabular-nums;
`;

const ExpandedPaymentDivider = styled.div`
    border-top: 1px solid ${colors.neutralDark};
    margin: 4px 0;
`;

/* ─── Sheet forms ─── */
const SheetForm = styled.div`display: flex; flex-direction: column; gap: 16px;`;

const FormField = styled.div`display: flex; flex-direction: column; gap: 6px;`;

const FormLabel = styled.label`
    font-size: 13px; font-weight: 600; color: ${colors.nightDark};
`;

const FormOptional = styled.span`
    font-weight: 400; color: ${colors.nightLight};
`;

const FormHint = styled.p`
    font-size: 12px; color: ${colors.nightLight}; margin: 0;
`;

const CurrencyWrap = styled.div`
    display: flex;
    align-items: center;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 12px;
    overflow: hidden;
    transition: border-color 0.15s;
    &:focus-within { border-color: ${colors.occasionalPurple}; }
`;

const CurrencyPrefix = styled.span`
    padding: 14px 0 14px 16px;
    font-size: 16px;
    font-weight: 700;
    color: ${colors.nightLight};
    flex-shrink: 0;
`;

const CurrencyInput = styled.input`
    flex: 1;
    border: none;
    padding: 14px 16px 14px 8px;
    font-size: 22px;
    font-weight: 800;
    color: ${colors.nightDark};
    outline: none;
    background: transparent;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.5px;
    &::placeholder { color: ${colors.neutralDark}; font-weight: 400; }
`;

const FormTextarea = styled.textarea`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 12px 16px;
    font-size: 15px;
    font-family: inherit;
    outline: none;
    color: ${colors.nightDark};
    background: #fff;
    width: 100%;
    box-sizing: border-box;
    resize: none;
    transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
    &::placeholder { color: ${colors.nightLight}; }
`;

const ErrorMsg = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.danger};
    padding: 10px 14px;
    background: ${colors.danger}08;
    border: 1px solid ${colors.danger}25;
    border-radius: 10px;
`;

const SubmitBtn = styled.button`
    padding: 15px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 700;
    border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: opacity 0.15s;
    background: ${({ disabled, $danger }) =>
        disabled ? colors.neutralDark : ($danger ? colors.danger : colors.success)};
    color: ${({ disabled }) => disabled ? colors.nightLight : '#fff'};
    &:hover:not(:disabled) { opacity: 0.9; }
`;

/* ─── Close sheet extras ─── */
const CloseExpectedRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 16px;
    background: ${colors.neutral};
    border-radius: 12px;
    text-align: center;
`;

const CloseExpectedLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const CloseExpectedValue = styled.span`
    font-size: 28px;
    font-weight: 800;
    color: ${colors.nightDark};
    letter-spacing: -0.5px;
    font-variant-numeric: tabular-nums;
`;

const CloseExpectedHint = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
`;

const DiffPreview = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    padding: 10px 14px;
    border-radius: 10px;
    background: ${({ $positive }) => $positive ? `${colors.success}08` : `${colors.danger}08`};
    color: ${({ $positive }) => $positive ? colors.successDark : colors.danger};
    border: 1px solid ${({ $positive }) => $positive ? `${colors.success}25` : `${colors.danger}25`};
`;

/* ─── PosEvent toggle + picker ─── */
const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: #f5f5f7;
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: #ededf0; }
    &:active { background: #e5e5e8; }
`;

const ToggleLabel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const ToggleTitle = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const ToggleHint = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${colors.nightLight};
`;

const TipSwitch = styled.div`
    width: 48px;
    height: 28px;
    border-radius: 14px;
    background: ${({ $active }) => $active ? colors.occasionalPurple : colors.neutralDark};
    padding: 2px;
    display: flex;
    align-items: center;
    transition: background 0.2s;
    flex-shrink: 0;
`;

const TipSwitchKnob = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.15);
    transform: translateX(${({ $active }) => $active ? '20px' : '0'});
    transition: transform 0.2s;
`;

const PosEventPicker = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 4px;
`;

const PosEventOption = styled.button`
    padding: 10px 14px;
    border-radius: 10px;
    border: 1.5px solid ${({ $selected }) => $selected ? colors.occasionalPurple : colors.neutralDark};
    background: ${({ $selected }) => $selected ? `${colors.occasionalPurple}12` : '#fff'};
    font-size: 14px;
    font-weight: ${({ $selected }) => $selected ? '700' : '500'};
    color: ${({ $selected }) => $selected ? colors.occasionalPurple : colors.night};
    cursor: pointer;
    text-align: left;
    transition: all 0.15s;
`;

const PickerHint = styled.p`
    font-size: 13px;
    color: ${colors.nightLight};
    margin: 4px 0;
`;

const EventBadge = styled.span`
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 8px;
    background: ${colors.occasionalPurple}18;
    color: ${colors.occasionalPurple};
    font-weight: 600;
    flex-shrink: 0;
    align-self: flex-start;
`;
